import React, { useState, useEffect } from "react";
import "./App.css";
import { ClientProvider } from "./context/ClientContext";
import ProSidebar from "./components/ProSidebar";
import GLPage from "./components/GLPage";
import GLCalculationsPage from "./components/GLCalculationsPage";
import UnmappedAccountsPage from "./components/UnmappedAccountsPage";
import ScheduleHPage from "./components/ScheduleHPage";
import DCAAAuditPage from "./components/DCAAAuditPage";
import ClientCloseoutPage from "./components/ClientCloseoutPage";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import OnboardingWizard from "./pages/OnboardingWizard";
import ClientManagementPage from "./pages/ClientManagementPage";
import ContractBriefingsPage from "./components/ContractBriefingsPage";
import ExecutiveDashboard from "./components/ExecutiveDashboard";
import RulesEditor from "./components/RulesEditor";
import ReconciliationDashboard from "./components/ReconciliationDashboard";
import ScheduleKPage from "./components/ScheduleKPage";
import ScheduleIPage from "./components/ScheduleIPage";
import ScheduleEPage from "./components/ScheduleEPage";
import ScheduleGPage from "./components/ScheduleGPage";
import ScheduleLPage from "./components/ScheduleLPage";
import ClientConfigView from "./components/ClientConfigView";
import API from './services/apiBase';
import { clearLocalProfile, clearStoredToken, getStoredToken, signOut } from './services/authSession';

// Legacy components import (for the "Legacy Features" tab)
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Upload, FileSpreadsheet, Calculator, TrendingUp, DollarSign, Target, Settings, History, AlertCircle, FileBarChart, FileText, Shield, Sparkles, Menu, Download, Database } from "lucide-react";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import Sidebar from "./components/Sidebar";
import MainContent from "./components/MainContent";
import ICESchedules from "./components/ICESchedules";
import AccountManagement from "./components/AccountManagement";
import AuditTrail from "./components/AuditTrail";
import VarianceMonitoring from "./components/VarianceMonitoring";
import AdvancedAllocations from "./components/AdvancedAllocations";
import ContractAllocations from "./components/ContractAllocations";
import GovernanceDashboard from "./components/GovernanceDashboard";
import AIAssistant from "./components/AIAssistant";

// Lightweight URL <-> Tab sync without react-router
const pathToTab = (path) => {
  // Handle dynamic client routes
  if (path.startsWith('/client-config/')) {
    return 'client-config';
  }
  
  switch (path) {
    case '/':
    case '':
      return 'home';
    case '/login':
      return 'login';
    case '/profile':
      return 'profile';
    case '/intake':
      return 'intake';
    case '/clients':
      return 'clients';
    case '/client-config':
      return 'client-config';
    case '/gl':
      return 'gl';
    case '/gl-calculations':
      return 'gl-calculations';
    case '/schedule-h':
      return 'schedule-h';
    case '/unmapped-accounts':
      return 'unmapped-accounts';
    case '/upload':
      return 'upload';
    case '/rates':
      return 'rates';
    case '/schedules':
      return 'schedules';
    case '/adjustments':
      return 'adjustments';
    case '/variance':
      return 'variance';
    case '/allocations':
      return 'allocations';
    case '/contracts':
      return 'contracts';
    case '/ai-assistant':
      return 'ai-assistant';
    case '/governance':
      return 'governance';
    case '/audit':
      return 'audit';
    case '/contract-briefings':
      return 'contract-briefings';
    case '/rules-debug':
      return 'rules-debug';
    case '/reconciliation':
      return 'reconciliation';
    case '/schedule-k':
      return 'schedule-k';
    case '/schedule-i':
      return 'schedule-i';
    case '/schedule-e':
      return 'schedule-e';
    default:
      return 'home';
  }
};

const tabToPath = (tab) => {
  switch (tab) {
    case 'home':
      return '/';
    case 'login':
      return '/login';
    case 'profile':
      return '/profile';
    case 'intake':
      return '/intake';
    case 'clients':
      return '/clients';
    case 'client-config':
      return '/client-config';
    case 'gl':
      return '/gl';
    case 'gl-calculations':
      return '/gl-calculations';
    case 'schedule-h':
      return '/schedule-h';
    case 'unmapped-accounts':
      return '/unmapped-accounts';
    case 'upload':
      return '/upload';
    case 'rates':
      return '/rates';
    case 'schedules':
      return '/schedules';
    case 'adjustments':
      return '/adjustments';
    case 'variance':
      return '/variance';
    case 'allocations':
      return '/allocations';
    case 'contracts':
      return '/contracts';
    case 'ai-assistant':
      return '/ai-assistant';
    case 'governance':
      return '/governance';
    case 'audit':
      return '/audit';
    case 'contract-briefings':
      return '/contract-briefings';
    case 'rules-debug':
      return '/rules-debug';
    case 'reconciliation':
      return '/reconciliation';
    case 'schedule-k':
      return '/schedule-k';
    case 'schedule-i':
      return '/schedule-i';
    case 'schedule-e':
      return '/schedule-e';
    default:
      return '/';
  }
};

function App() {
  // State management
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [calculations, setCalculations] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [trialBalanceAccounts, setTrialBalanceAccounts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  
  // Active tab for new UI
  const [activeTab, setActiveTab] = useState('home');
  
  // Legacy tab state
  const [legacyTab, setLegacyTab] = useState('upload');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Manual adjustments state
  const [adjustedRates, setAdjustedRates] = useState(null);
  const [affectedSchedules, setAffectedSchedules] = useState([]);

  // Trial balance state for enhanced UI
  const [trialBalance, setTrialBalance] = useState(null);
  
  const clearSession = () => {
    clearStoredToken();
    clearLocalProfile();
  };

  useEffect(() => {
    // Bootstrap auth state on first load and recover user/session details.
    const initializeSession = async () => {
      const token = getStoredToken();
      const publicPages = ['login', 'profile'];  // Profile is accessible after login but doesn't need calculations

      if (!token) {
        if (!publicPages.includes(activeTab)) {
          setActiveTab('login');
          window.history.replaceState({}, '', '/login');
        }
        return;
      }

      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Session validation failed: ${response.status}`);
        }

        const user = await response.json();
        localStorage.setItem('user', JSON.stringify(user));
        if (!localStorage.getItem('active_client_id') && user?.client_id) {
          localStorage.setItem('active_client_id', user.client_id);
        }

        const pagesNeedingCalculations = ['home', 'gl', 'gl-calculations', 'schedule-h', 'unmapped-accounts'];
        if (pagesNeedingCalculations.includes(activeTab)) {
          loadCalculations();
        }
      } catch (e) {
        console.warn('Session initialization failed, forcing login:', e);
        clearSession();
        setActiveTab('login');
        window.history.replaceState({}, '', '/login');
      }
    };

    initializeSession();
  }, []); // Run only once on mount

  // Separate effect for URL sync
  useEffect(() => {
    // Sync initial tab from URL
    const initialPath = window.location.pathname;
    const initialTab = pathToTab(initialPath);
    if (initialTab !== activeTab) {
      setActiveTab(initialTab);
    }

    // Handle back/forward navigation
    const onPop = () => {
      const tab = pathToTab(window.location.pathname);
      setActiveTab(tab);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Push path on tab change
  useEffect(() => {
    const path = tabToPath(activeTab);
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }, [activeTab]);

  const loadCalculations = async () => {
    try {
      console.log('Loading calculations from:', `${API}/calculations`);
      const response = await fetch(`${API}/calculations`);
      if (response.ok) {
        const data = await response.json();
        console.log('Calculations loaded successfully:', data.length, 'calculations');
        setCalculations(data);
      } else {
        console.error('Error loading calculations - HTTP Status:', response.status);
      }
    } catch (e) {
      console.error('Error loading calculations - Network/Parse error:', e);
      setCalculations([]);
    }
  };

  const renderMainContent = () => {
    // Handle legacy sub-tabs
    const legacyTabs = ['upload', 'rates', 'schedules', 'adjustments', 'variance', 'allocations', 'contracts', 'ai-assistant', 'governance'];
    
    if (legacyTabs.includes(activeTab)) {
      // Render legacy content in the main area (without the old sidebar)
      return (
        <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
          <MainContent
            activeTab={activeTab}
            calculation={calculation}
            trialBalanceAccounts={trialBalanceAccounts}
            setTrialBalanceAccounts={setTrialBalanceAccounts}
            adjustments={adjustments}
            setAdjustments={setAdjustments}
          />
        </div>
      );
    }

    switch (activeTab) {
      case 'login':
        return <LoginPage />;
      case 'profile':
        return <ProfilePage />;
      case 'intake':
        return <OnboardingWizard onCancel={() => {
          setActiveTab('clients');
          window.history.pushState({}, '', '/clients');
        }} />;
      case 'clients':
        return <ClientManagementPage />;
      case 'client-config':
        const activeClientId = localStorage.getItem('active_client_id');
        return (
          <ClientConfigView 
            clientId={activeClientId} 
            onBack={() => {
              setActiveTab('clients');
              window.history.pushState({}, '', '/clients');
            }} 
          />
        );
      case 'home':
        return (
          <div style={{ padding: '2rem', maxWidth: '100%', margin: '0 auto' }}>
            {/* Executive Dashboard */}
            <ExecutiveDashboard onNavigate={setActiveTab} />
          </div>
        );

      case 'gl':
        return <GLPage />;

      case 'gl-calculations':
        return <GLCalculationsPage />;

      case 'schedule-h':
        return <ScheduleHPage />;

      case 'unmapped-accounts':
        return <UnmappedAccountsPage />;

      case 'audit':
        return <DCAAAuditPage />;

      case 'client-closeout':
        return <ClientCloseoutPage />;

      case 'contract-briefings':
        return <ContractBriefingsPage />;

      case 'rules-debug':
        return <RulesEditor token={getStoredToken()} />;

      case 'reconciliation':
        return <ReconciliationDashboard />;

      case 'schedule-k':
        return <ScheduleKPage />;

      case 'schedule-i':
        return <ScheduleIPage />;

      case 'schedule-e':
        return <ScheduleEPage />;

      case 'schedule-g':
        return <ScheduleGPage />;

      case 'schedule-l':
        return <ScheduleLPage />;

      default:
        return null;
    }
  };

  // Check if we're on a standalone page (login/profile/intake only)
  const isStandalonePage = activeTab === 'login' || activeTab === 'profile' || activeTab === 'intake';
  
  // Get current user for header display
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const hasToken = Boolean(getStoredToken());

  return (
    <ClientProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--gray-50)' }}>
        {/* Professional Sidebar - Hide on standalone pages */}
        {!isStandalonePage && (
          <ProSidebar activeTab={activeTab} setActiveTab={setActiveTab} calculation={calculation} />
        )}

        {/* Main Content Area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header - Hide on standalone pages */}
          {!isStandalonePage && (
            <div className="pro-header">
              <div>
                <h1 className="pro-header-title">IncurredIQ</h1>
                <p className="pro-header-subtitle">Professional financial management and compliance tools</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="pro-badge pro-badge-success">All Systems Operational</span>
                
                {/* User Menu */}
                {currentUser.email && (
                  <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-300">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{currentUser.full_name || currentUser.email}</div>
                      <div className="text-xs text-gray-500">
                        {currentUser.role?.replace('_', ' ').toUpperCase()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setActiveTab('profile')}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          signOut();
                        }}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}

                {!currentUser.email && hasToken && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-300">
                    <span className="text-xs text-amber-700">Session needs refresh</span>
                    <button
                      onClick={() => {
                        clearSession();
                        window.location.href = '/login';
                      }}
                      className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                    >
                      Reset Session
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {renderMainContent()}
        </div>
      </div>
    </ClientProvider>
  );
}

export default App;
